import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import Hls from "hls.js";
import { Workbox } from "workbox-window";
import { App } from "./App";
import { ApiClient } from "./api/client";
import { ApiClientContext } from "./api/ApiClientContext";
import { AuthProvider, TOKEN_STORAGE_KEY } from "./auth/AuthContext";
import { clearSignoutReason, stashSignoutReason } from "./auth/signoutReason";
import { setServicePaused } from "./paused";
import "@photo-sphere-viewer/core/index.css";
import "./viewer-overrides.css";

// The shared viewer reads `globalThis.Hls` (mirroring its `globalThis.MP4Box`
// pattern) to attach hls.js when an `hlsSrc` is supplied.
(globalThis as unknown as { Hls: unknown }).Hls = Hls;

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

const client = new ApiClient({
  baseUrl,
  getToken: () => localStorage.getItem(TOKEN_STORAGE_KEY),
  onUnauthorized: (reason) => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    if (window.location.pathname !== "/login") {
      // Stash before navigating: the login page picks the reason up as it
      // mounts and says out loud why the tab was signed out (spec §17).
      if (reason !== undefined) stashSignoutReason(reason);
      window.location.assign("/login");
    } else {
      // Already on the login page, so nothing is about to mount and read it.
      // Anything left in the stash would surface on the *next* sign-out and
      // blame the wrong device.
      clearSignoutReason();
    }
  },
  onPaused: () => setServicePaused(true),
});

// Register the Workbox-generated service worker in production builds only.
// Dev mode disables vite-plugin-pwa to avoid Workbox interfering with HMR.
// The `waiting` handler silently skip-waits so SW updates apply on next reload.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  const wb = new Workbox("/service-worker.js");
  wb.addEventListener("waiting", () => {
    void wb.messageSkipWaiting();
  });
  void wb.register();
}

// E2E-only: install `window.__vmcTestSeed` when built with VITE_E2E=1 so
// Playwright can drive the offline IndexedDB store without standing up the
// Worker API. Gated on a build-time env var so the helper is tree-shaken out
// of normal production builds.
if (import.meta.env.VITE_E2E === "1") {
  void import("./test-seed").then((m) => m.installTestSeed());
}

const root = document.getElementById("root");
if (root === null) throw new Error("missing #root");

createRoot(root).render(
  <StrictMode>
    <ApiClientContext.Provider value={client}>
      <AuthProvider client={client}>
        <App />
      </AuthProvider>
    </ApiClientContext.Provider>
  </StrictMode>,
);
